"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ShieldCheck, CircleCheck, Loader, User, Phone, MapPin, FileText, MessageSquare } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function AgendarEvaluacion() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="agendar-evaluación" className="relative w-full py-24 overflow-hidden !bg-[hsl(173,80%,40%)]">
      {}
      <div className="absolute -top-24 -right-24 opacity-5 pointer-events-none select-none">
        <ShieldCheck className="w-96 h-96 text-primary rotate-12" />
      </div>
      
      <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-primary/5 to-transparent pointer-events-none" />

      <div ref={ref} className={cn("max-w-3xl mx-auto px-6 relative z-10 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-extrabold text-foreground mb-4 tracking-tight">
            Agendar Evaluación
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Déjenos sus datos y un coordinador de NexoMayor se contactará con usted para entender su situación y proponer el mejor plan de acción.
          </p>
        </div>

        <Card className="border-border/50 shadow-xl shadow-primary/5 bg-card/80 backdrop-blur-sm overflow-hidden">
          <div className="bg-primary/5 px-8 py-4 border-b border-border/50 flex items-center justify-between">
            <span className="text-sm font-medium text-primary flex items-center gap-2">
              <ShieldCheck className="size-4" />
              Información Segura y Confidencial
            </span>
            <span className="text-xs text-muted-foreground">Paso único</span>
          </div>
          
          <Progress value={100} className="h-1 rounded-none bg-primary/10" />

          <CardContent className="p-8 md:p-10">
            <FormProvider formId="agendar_evaluacion_contact" sectionName="Agendar Evaluación" defaultValues={{
            nombre: "",
            telefono: "",
            comuna: "",
            situacion: "",
            preferencia_contacto: "WhatsApp"
          }} validationRules={{
            nombre: {
              required: true,
              minLength: 2
            },
            telefono: {
              required: true,
              minLength: 8
            },
            comuna: {
              required: true
            },
            situacion: {
              required: true,
              minLength: 10
            },
            preferencia_contacto: {
              required: true
            }
          }} submitText="Solicitar Evaluación" successMessage="¡Gracias por contactarnos! Hemos recibido su solicitud y nos comunicaremos a la brevedad.">
              <EvaluacionForm />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}
function EvaluacionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 bg-primary/10 rounded-full flex items-center justify-center">
          <CircleCheck className="size-10 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">¡Solicitud Recibida!</h3>
          <p className="text-muted-foreground max-w-md mx-auto text-lg">
            {successMessage}
          </p>
        </div>
        <p className="text-sm text-muted-foreground bg-muted/50 px-4 py-2 rounded-md">
          Un coordinador de NexoMayor revisará su caso de forma confidencial.
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      {}
      <div className="space-y-6">
        <div className="flex items-center gap-2 pb-2 border-b border-border/50">
          <User className="size-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Datos de Contacto</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="nombre" rules={{
          required: "El nombre es requerido"
        }} render={({
          field
        }) => <FormItem className="relative">
                <FormControl>
                  <Input {...field} className="peer pt-6 pb-2 h-14 bg-background/50 focus:bg-background transition-colors text-base" placeholder=" " />
                </FormControl>
                <FormLabel className="absolute left-3 top-2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:text-xs pointer-events-none">
                  Nombre Completo
                </FormLabel>
                <FormDescription className="text-xs">
                  Ej: Juan Pérez
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="telefono" rules={{
          required: "El teléfono es requerido"
        }} render={({
          field
        }) => <FormItem className="relative">
                <FormControl>
                  <Input {...field} type="tel" className="peer pt-6 pb-2 h-14 bg-background/50 focus:bg-background transition-colors text-base" placeholder=" " />
                </FormControl>
                <FormLabel className="absolute left-3 top-2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:text-xs pointer-events-none">
                  Teléfono Móvil
                </FormLabel>
                <FormDescription className="text-xs">
                  Ej: +56 9 1234 5678
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="comuna" rules={{
          required: "La comuna es requerida"
        }} render={({
          field
        }) => <FormItem className="relative md:col-span-2">
                <FormControl>
                  <Input {...field} className="peer pt-6 pb-2 h-14 bg-background/50 focus:bg-background transition-colors text-base" placeholder=" " />
                </FormControl>
                <FormLabel className="absolute left-3 top-2 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:text-xs pointer-events-none">
                  Comuna de Residencia
                </FormLabel>
                <FormDescription className="text-xs">
                  Comuna donde reside la persona mayor (Ej: Santiago Centro)
                </FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-center gap-2 pb-2 border-b border-border/50">
          <FileText className="size-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Detalles de la Situación</h3>
        </div>

        <FormField control={control} name="situacion" rules={{
        required: "Por favor describa la situación"
      }} render={({
        field
      }) => <FormItem className="relative">
              <FormControl>
                <Textarea {...field} className="peer pt-8 pb-3 min-h-[140px] bg-background/50 focus:bg-background transition-colors text-base resize-y" placeholder=" " />
              </FormControl>
              <FormLabel className="absolute left-3 top-3 text-xs text-muted-foreground transition-all peer-placeholder-shown:top-6 peer-placeholder-shown:text-base peer-focus:top-3 peer-focus:text-xs pointer-events-none">
                Situación / Necesidad
              </FormLabel>
              <FormDescription className="text-xs">
                Describa brevemente la situación o necesidad de la persona mayor. Cuanta más información, mejor podremos preparar la evaluación.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferencia_contacto" rules={{
        required: "Seleccione una preferencia"
      }} render={({
        field
      }) => <FormItem className="space-y-4 bg-muted/30 p-5 rounded-xl border border-border/50">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="size-4 text-muted-foreground" />
                <FormLabel className="text-base font-medium text-foreground">
                  Preferencia de contacto
                </FormLabel>
              </div>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                  {["WhatsApp", "Llamada telefónica", "Correo electrónico"].map((option, i) => <FormItem key={option} data-index={i} className="flex items-center space-x-3 space-y-0">
                      <FormControl>
                        <RadioGroupItem value={option} className="size-5" />
                      </FormControl>
                      <FormLabel className="font-normal text-base cursor-pointer">
                        {option}
                      </FormLabel>
                    </FormItem>)}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <div className="size-5 rounded-full bg-destructive/20 flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-destructive text-xs font-bold">!</span>
          </div>
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold shadow-lg hover:shadow-xl transition-all bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Enviando solicitud...
          </> : 'Agendar Evaluación'}
      </Button>
      
      <p className="text-center text-xs text-muted-foreground mt-4">
        Al enviar este formulario, usted acepta que NexoMayor se ponga en contacto con usted para fines de evaluación.
      </p>
    </Form>;
}